import axios from "axios";
import { config } from "./runtimeConfig";

/**
 * Auth state is owned by React (context/AuthContext.jsx) but axios
 * interceptors run outside the component tree, so AuthBridge (rendered once
 * near the root -- see App.jsx) pushes the latest auth values in here on
 * every change. Every request gets: Bearer token, X-Device-Fingerprint,
 * X-Session-Type, X-Stay-Logged-In -- matching what
 * api/services/auth.py's secure_endpoint expects on every call.
 */
let authState = {
  getAccessToken: async () => null,
  deviceFingerprint: "",
  sessionType: "web",
  stayLoggedIn: false,
  // Which account the user is working in (#310). null means their own,
  // which is always the default -- the API never infers it.
  actingAccountId: null,
  stepUpMfa: () => {},
  onUnauthorized: () => {},
  onNotProvisioned: () => {},
  onAccountSuspended: () => {},
  onAuthMisconfigured: () => {},
};

/**
 * 401 codes where signing in again is genuinely the remedy, and therefore
 * the only ones that may trigger a logout.
 *
 * Everything else that arrives as a 401 is a dead end the user cannot
 * escape by re-authenticating, and logging them out for it produces an
 * unbreakable loop -- sign in, bounce to the Microsoft sign-out page, sign
 * in again -- with no message, and with the redirect wiping the console and
 * network log on the way past (#166). Deliberately an allowlist: a new
 * error code added on the API side should default to "show the user
 * something", not to "throw them out".
 */
const CODES_THAT_MEAN_SIGN_IN_AGAIN = new Set([
  "unauthorized", // bad, malformed or expired token
]);

export function setApiAuthState(next) {
  authState = { ...authState, ...next };
}

export const apiClient = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: 20000,
});

apiClient.interceptors.request.use(async (req) => {
  const token = await authState.getAccessToken();
  if (token) req.headers.Authorization = `Bearer ${token}`;
  req.headers["X-Device-Fingerprint"] = authState.deviceFingerprint;
  req.headers["X-Session-Type"] = authState.sessionType;
  req.headers["X-Stay-Logged-In"] = String(authState.stayLoggedIn);
  // Only sent when working in somebody else's account. Absent means "my
  // own", so a client that never sets it behaves exactly as before -- and
  // the API defaults to the safe answer rather than guessing (#310).
  if (authState.actingAccountId) {
    req.headers["X-Acting-Account"] = String(authState.actingAccountId);
  }
  return req;
});

apiClient.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error.response?.status;
    const code = error.response?.data?.error;

    if (status === 401 && code === "mfa_required") {
      authState.stepUpMfa();
    } else if (status === 401 && code === "reauth_required") {
      // Absolute session age cap (#457). A silent token refresh would
      // succeed and change nothing, so this must NOT be treated as
      // `unauthorized` -- it needs an interactive sign-in.
      authState.onReauthRequired?.(error.response?.data?.message);
    } else if (status === 401 && code === "not_provisioned") {
      // Valid token, real identity, no app.Users row. Signing in again
      // cannot create one, so show it and stay put.
      authState.onNotProvisioned(error.response?.data?.message);
    } else if (status === 401 && code === "account_suspended") {
      // An admin removed this account. Signing in again cannot undo that,
      // and it must NOT be treated as a normal 401 -- logging them out
      // would loop them through the identity provider forever (#166), and
      // provisioning already refuses to hand them a fresh account (#305).
      authState.onAccountSuspended(error.response?.data?.message);
    } else if (status === 401 && code === "auth_misconfigured") {
      // Wrong tenant, wrong audience, unreachable signing keys. An
      // operator problem; logging the user out would hide it.
      authState.onAuthMisconfigured(error.response?.data?.message);
    } else if (status === 401 && CODES_THAT_MEAN_SIGN_IN_AGAIN.has(code)) {
      authState.onUnauthorized();
    } else if (status === 401) {
      // An unrecognised 401 code. Surfacing it beats a silent logout: the
      // worst case is a message the user reports, rather than a loop
      // nobody can diagnose.
      authState.onAuthMisconfigured(error.response?.data?.message);
    }

    // Normalize so callers can do `catch (err) { err.apiMessage }` without
    // digging through axios's response shape every time.
    error.apiMessage = error.response?.data?.message || error.message;
    error.apiErrorCode = code;
    // The status, normalised alongside the message, so callers can tell a
    // REFUSAL from a failure without reaching into axios's response shape
    // (#245). Undefined when the request never got an answer at all --
    // which is exactly the case a retry is for.
    error.apiStatus = status;
    return Promise.reject(error);
  }
);


/**
 * Did the server REFUSE this, as opposed to failing to answer? (#245)
 *
 * A 403 is the server's answer: this account may not have that. Retrying it
 * produces the same 403 forever, so offering "Try again" is an affordance
 * that cannot work -- and it sends the user to check their connection over
 * something that has nothing to do with their connection.
 *
 * The mobile side has followed this rule since #166 (`if (err.response)
 * throw err` -- only a network failure earns a cached fallback). This is the
 * web half of the same idea.
 */
export function isRefused(err) {
  return err?.apiStatus === 403;
}
